import React, { useState, useEffect } from "react";
import Modal from "components/modal";
import { MdInfo } from "react-icons/md";

const RoleFormModal = ({ 
  isOpen, 
  onClose, 
  onSubmit, 
  initialRole, 
  isEditing,
  existingRoles = [],
  loading 
}) => {
  const [role, setRole] = useState({
    role_name: "",
    role_description: "",
    privilege: 4
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Générer les options de privilèges (1-10)
  const privilegeOptions = Array.from({ length: 10 }, (_, i) => i + 1);
  
  // Noms des privilèges courants
  const privilegeNames = {
    1: "Admin (Maximal)",
    2: "Élevé",
    3: "Moyen",
    4: "Standard",
  };

  useEffect(() => {
    if (isOpen && initialRole) {
      setRole({
        role_name: initialRole.role_name || "",
        role_description: initialRole.role_description || "",
        privilege: initialRole.privilege || 4,
      });
      setValidationErrors({});
    }
  }, [isOpen, initialRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRole({
      ...role,
      [name]: name === "privilege" ? parseInt(value, 10) : value,
    });

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!role.role_name.trim()) {
      errors.role_name = "Le nom du rôle est requis";
    } else if (
      !isEditing &&
      existingRoles.some(r => 
        r.role_name.toLowerCase() === role.role_name.toLowerCase()
      )
    ) {
      errors.role_name = "Ce nom de rôle existe déjà";
    }

    if (!role.role_description.trim()) {
      errors.role_description = "La description du rôle est requise";
    }

    if (
      !role.privilege ||
      isNaN(role.privilege) ||
      role.privilege < 1 ||
      role.privilege > 10
    ) {
      errors.privilege = "Le niveau de privilège doit être entre 1 et 10";
    }

    // Vérifier si le niveau de privilège est déjà utilisé
    const isPrivilegeUsed = existingRoles.some(r => 
      r.privilege === role.privilege && (!isEditing || r.role_id !== initialRole.role_id)
    );
    
    if (isPrivilegeUsed) {
      errors.privilege = "Ce niveau de privilège est déjà utilisé par un autre rôle";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    
    const result = await onSubmit({
      role_name: role.role_name,
      role_description: role.role_description,
      privilege: role.privilege,
    });
    
    if (!result.success) {
      setValidationErrors(result.errors || {});
    }
    
    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="md">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing
              ? "Modifier le rôle"
              : "Créer un nouveau rôle"}
          </h3>
          <div className="h-1 w-12 bg-[#57A863] mt-2 rounded-full"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label
              htmlFor="role_name"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Nom du rôle
            </label>
            <input
              type="text"
              name="role_name"
              id="role_name"
              value={role.role_name}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              placeholder="Ex: Administrateur, Éditeur..."
            />
            {validationErrors.role_name && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.role_name}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="role_description"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Description
            </label>
            <input
              type="text"
              name="role_description"
              id="role_description"
              value={role.role_description}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              placeholder="Ex: Administrateur système, Editeur de contenu..."
            />
            {validationErrors.role_description && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.role_description}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="privilege"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Niveau de privilège
            </label>
            <select
              name="privilege"
              id="privilege"
              value={role.privilege}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-[#57A863] focus:outline-none focus:ring-[#57A863]"
              disabled={isEditing && initialRole.privilege === 1} // Empêcher la modification du niveau Admin
            >
              {privilegeOptions.map((level) => (
                <option key={level} value={level}>
                  {level} - {privilegeNames[level] || `Niveau ${level}`}
                </option>
              ))}
            </select>
            {validationErrors.privilege && (
              <p className="mt-1.5 text-sm text-red-600">
                {validationErrors.privilege}
              </p>
            )}
            
            <div className="mt-2 rounded-md bg-blue-50 p-3 border border-blue-100">
              <div className="flex">
                <div className="flex-shrink-0">
                  <MdInfo className="h-5 w-5 text-blue-600" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Note :</strong> Plus le niveau est bas, plus les privilèges sont élevés. 
                    Le niveau 1 correspond à l'administrateur avec tous les droits.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {validationErrors.general && (
            <div className="rounded-md bg-red-50 p-3 border border-red-200">
              <p className="text-sm text-red-600">
                {validationErrors.general}
              </p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="px-4 py-2 rounded-md text-gray-600 border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 rounded-md bg-[#57A863] text-white hover:bg-[#4c9657] transition-colors shadow-sm"
              disabled={submitting || loading || (isEditing && initialRole.privilege === 1)}
            >
              {(submitting || loading) ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Créer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default RoleFormModal;